import { zodResolver } from '@hookform/resolvers/zod';
import { FC } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Button, Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';
import { enqueueSnackbar } from 'notistack';

import { ObjectDocFormValues, createObjectDocSchema } from 'api/objectDocs/type';
import { MLInput } from 'components/Input/Input';
import usePostObjectDoc from 'hooks/ObjectDocs/usePostObjectDoc';
import useGetObjectDocs from 'hooks/ObjectDocs/useGetObjectDocs';

interface AddObjectDocModalProps {
  slug?: string;
  isOpen: boolean;
  onClose: () => void;
}

const AddObjectDocModal: FC<AddObjectDocModalProps> = ({ slug, isOpen, onClose }) => {
  const { t } = useTranslation();
  const { createObjectDoc } = usePostObjectDoc();
  const { refetch } = useGetObjectDocs(slug || '');
  const { handleSubmit, control } = useForm<ObjectDocFormValues>({
    resolver: zodResolver(createObjectDocSchema),
    defaultValues: {
      doc_name: '',
      file: undefined,
    },
  });

  const onAddObjectDoc = (data: ObjectDocFormValues) => {
    createObjectDoc(
      { document: data, slug: slug || '' },
      {
        onSuccess: () => {
          enqueueSnackbar('Document added successfully', { variant: 'success' });
          refetch();
          onClose();
        },
      },
    );
  };

  return (
    <Modal isOpen={isOpen} toggle={onClose} size="md">
      <ModalHeader className="border-bottom py-3 mx-2" toggle={onClose}>
        {t('ObjectDocs.add-modal.title')}
      </ModalHeader>
      <ModalBody>
        <div className="row m-2">
          <div className="col-md-12">
            <Controller
              control={control}
              name="doc_name"
              render={({ field, fieldState: { error } }) => (
                <MLInput
                  type="text"
                  label={t('ObjectDocs.add-modal.doc-name')}
                  error={error?.message}
                  required
                  {...field}
                />
              )}
            />
          </div>
        </div>
        <div className="row m-2">
          <div className="col-md-12">
            <Controller
              control={control}
              name="file"
              render={({ field, fieldState: { error } }) => (
                <MLInput
                  type="file"
                  label={t('ObjectDocs.add-modal.file')}
                  error={error?.message}
                  required
                  onChange={e => {
                    const files = e.target.files;
                    if (files && files.length > 0) {
                      field.onChange(files[0]);
                    }
                  }}
                />
              )}
            />
          </div>
        </div>
      </ModalBody>
      <ModalFooter>
        <Button className="btn btn-ghost-success" color="success" onClick={handleSubmit(onAddObjectDoc)}>
          {t('ObjectDocs.add-modal.submit-btn')}
        </Button>{' '}
        <Button
          className="btn btn-ghost-danger"
          color="danger"
          onClick={() => {
            onClose();
          }}
        >
          {t('ObjectDocs.add-modal.cancel-btn')}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default AddObjectDocModal;
